import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import Icon from './Icon.jsx';
import StoryMap from './StoryMap.jsx';
import StoryIllumination from './StoryIllumination.jsx';
import {
  actProgress,
  chapterStatus,
  completedCount,
  FREE_CHAPTERS,
  MAX_STARS,
  nextChapter,
  STORY_TOTAL,
  totalStars,
} from '../lib/story.js';

const ROMAN = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII'];

/**
 * The story map — a trail that winds down the page.
 *
 * Chapters alternate left and right of centre and are joined by a drawn curve,
 * so the eye is pulled downward through the arc rather than reading a list. The
 * curve is a real SVG path measured from the laid-out cards rather than a
 * decorative background, because the cards are different heights and any fixed
 * geometry would drift out of alignment as titles wrap.
 */
const VIEWS = [
  { id: 'trail', label: 'Trail', glyph: 'path' },
  { id: 'map', label: 'Map', glyph: 'globe' },
  { id: 'page', label: 'Page', glyph: 'scroll' },
];

/**
 * Three views of one journey.
 *
 * Deliberately a switcher inside this screen rather than three nav tabs. The
 * trail, the map and the illuminated page are the same twenty-eight chapters
 * and the same progress — presenting them as separate destinations would treble
 * the places to go without adding anything to do.
 */
export default function StoryScreen({ story, isPremium, onOpenChapter, onUpgrade }) {
  const [view, setView] = useState('trail');
  const acts = useMemo(() => actProgress(story), [story]);
  const done = completedCount(story);
  const stars = totalStars(story);
  const next = nextChapter(story);

  const trailRef = useRef(null);
  const nodeRefs = useRef(new Map());
  const currentRef = useRef(null);
  const [path, setPath] = useState('');
  const [reached, setReached] = useState(0);

  /**
   * Build the connecting curve from the actual card positions.
   *
   * Each segment leaves the bottom of one card and enters the top of the next
   * with vertical control points, which is what gives the trail its S-bend
   * instead of a straight diagonal.
   */
  const measure = useCallback(() => {
    const container = trailRef.current;
    if (!container) return;

    /**
     * Position relative to the trail, walked up the offsetParent chain.
     *
     * Deliberately not getBoundingClientRect: the screen fades in on a
     * `translateY`, and a rect measured mid-animation bakes that offset into
     * the curve permanently — a ResizeObserver never fires for a transform, so
     * nothing would correct it. Layout offsets ignore transforms entirely.
     */
    const offsetIn = (el) => {
      let x = 0;
      let y = 0;
      let node = el;
      while (node && node !== container) {
        x += node.offsetLeft;
        y += node.offsetTop;
        node = node.offsetParent;
      }
      return { x, y };
    };

    const points = [];
    for (const [, el] of [...nodeRefs.current.entries()].sort((a, b) => a[0] - b[0])) {
      if (!el) continue;
      const { x, y } = offsetIn(el);
      points.push({
        x: x + el.offsetWidth / 2,
        top: y,
        bottom: y + el.offsetHeight,
      });
    }
    if (points.length < 2) {
      setPath('');
      return;
    }

    let d = '';
    for (let i = 0; i < points.length - 1; i++) {
      const from = points[i];
      const to = points[i + 1];
      const lift = Math.max(28, (to.top - from.bottom) * 0.65);
      d += `M ${from.x} ${from.bottom} C ${from.x} ${from.bottom + lift}, ${to.x} ${
        to.top - lift
      }, ${to.x} ${to.top} `;
    }
    setPath(d.trim());

    // How far down the trail the player has reached — used to fill the curve
    // gold up to the current chapter and leave the rest faint.
    const doneNodes = points.slice(0, Math.max(1, done));
    setReached(doneNodes.length ? doneNodes[doneNodes.length - 1].bottom : 0);
  }, [done]);

  useLayoutEffect(() => {
    measure();
    const container = trailRef.current;
    if (!container || typeof ResizeObserver === 'undefined') return undefined;
    // Cards change height when titles rewrap, so remeasure on resize rather
    // than only on mount.
    const observer = new ResizeObserver(measure);
    observer.observe(container);
    for (const el of nodeRefs.current.values()) if (el) observer.observe(el);
    return () => observer.disconnect();
  }, [measure]);

  // Fonts land after first paint and shift every card, so measure again once
  // they're ready rather than leaving the curve attached to the wrong places.
  useEffect(() => {
    document.fonts?.ready?.then(measure).catch(() => {});
  }, [measure]);

  // Bring the chapter they're up to into view, but only on arrival — scrolling
  // the page out from under someone browsing back through Act I would be worse
  // than making them scroll.
  useEffect(() => {
    currentRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  let flat = -1;

  return (
    <section className="screen story-screen">
      <div className="screen__heading">
        <h2>The Story</h2>
        <p>
          The whole Bible as one narrative, in {STORY_TOTAL} chapters{' '}
          <span className="nowrap">
            · <strong>{done} read</strong>
          </span>
        </p>
      </div>

      <div className="seg seg--views" role="tablist" aria-label="How to see the story">
        {VIEWS.map((option) => (
          <button
            key={option.id}
            type="button"
            role="tab"
            aria-selected={view === option.id}
            className={`seg__btn${view === option.id ? ' is-on' : ''}`}
            onClick={() => setView(option.id)}
          >
            <Icon name={option.glyph} size={14} />
            {option.label}
          </button>
        ))}
      </div>

      {view === 'map' ? (
        <StoryMap
          story={story}
          isPremium={isPremium}
          onOpenChapter={onOpenChapter}
          onUpgrade={onUpgrade}
        />
      ) : view === 'page' ? (
        <StoryIllumination
          story={story}
          isPremium={isPremium}
          onOpenChapter={onOpenChapter}
          onUpgrade={onUpgrade}
        />
      ) : (
        <>

      <div className="story-rollup">
        <div className="story-rollup__bar">
          <span style={{ width: `${(done / STORY_TOTAL) * 100}%` }} />
        </div>
        <span className="story-rollup__stars">
          <Icon name="star" size={13} />
          {stars} / {STORY_TOTAL * MAX_STARS}
        </span>
      </div>

      {done === 0 ? (
        <p className="hint">
          Sixty-six books, one storyline. Start at creation and walk it through to the new
          creation — each chapter is a few minutes, an anchor verse, and one question.
        </p>
      ) : null}

      <div className="trail" ref={trailRef}>
        {/* The curve sits behind the cards and is drawn twice: once faint for
            the whole route, once gold and clipped to how far you've come. */}
        <svg className="trail__svg" aria-hidden="true" preserveAspectRatio="none">
          <defs>
            <linearGradient id="trail-walked" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="var(--gold)" />
              <stop offset="100%" stopColor="var(--gold-soft)" />
            </linearGradient>
            <clipPath id="trail-clip">
              <rect x="0" y="0" width="10000" height={reached} />
            </clipPath>
          </defs>
          <path className="trail__route" d={path} />
          <path className="trail__walked" d={path} clipPath="url(#trail-clip)" />
        </svg>

        {acts.map((act, actIndex) => (
          <section className="trail__act" key={act.id}>
            <header className={`act-card${act.complete ? ' is-complete' : ''}`}>
              <span className="act-card__numeral" aria-hidden="true">
                {ROMAN[actIndex]}
              </span>
              <div className="act-card__titles">
                <h3>{act.title}</h3>
                <p>{act.summary}</p>
                <span className="act-card__meta">
                  {act.span} · {act.done}/{act.total} read · {act.stars}/{act.starsTotal}{' '}
                  <Icon name="star" size={11} />
                </span>
              </div>
              {act.complete ? (
                <span className="act-card__seal" title="Act complete">
                  <Icon name="check" size={15} />
                </span>
              ) : null}
            </header>

            {!isPremium && act.chapters.some((c) => c.index === FREE_CHAPTERS) ? (
              <button type="button" className="story-gate" onClick={onUpgrade}>
                <Icon name="radiance" size={18} />
                <span>
                  <strong>The story continues here</strong>
                  <span>
                    The free tier covers the first {FREE_CHAPTERS} chapters. Unlock the
                    remaining {STORY_TOTAL - FREE_CHAPTERS} to reach the new creation.
                  </span>
                </span>
              </button>
            ) : null}

            {act.chapters.map((chapter) => {
              flat += 1;
              const index = flat;
              const status = chapterStatus(story, chapter.index, isPremium);
              const isNext = next?.id === chapter.id;
              const openable = status === 'done' || status === 'current';
              // Alternating sides are what make the trail wind. Odd chapters
              // sit right, even ones left.
              const side = index % 2 === 0 ? 'left' : 'right';

              return (
                <div
                  className={`stop stop--${side} stop--${status}${isNext ? ' is-next' : ''}`}
                  key={chapter.id}
                  ref={isNext ? currentRef : undefined}
                >
                  <span className="stop__label" aria-hidden="true">
                    <em>Chapter</em>
                    <b>{chapter.order}</b>
                  </span>

                  <button
                    type="button"
                    className="stop__card"
                    ref={(el) => {
                      if (el) nodeRefs.current.set(index, el);
                      else nodeRefs.current.delete(index);
                    }}
                    onClick={() =>
                      status === 'premium'
                        ? onUpgrade()
                        : openable
                          ? onOpenChapter(chapter.id)
                          : undefined
                    }
                    disabled={status === 'locked'}
                    aria-label={
                      status === 'locked'
                        ? `Chapter ${chapter.order}, ${chapter.title} — locked until you finish the previous chapter`
                        : status === 'premium'
                          ? `Chapter ${chapter.order}, ${chapter.title} — unlock with premium`
                          : `Chapter ${chapter.order}, ${chapter.title}`
                    }
                  >
                    <span className="stop__badge" aria-hidden="true">
                      {status === 'done' ? (
                        <Icon name="check" size={22} />
                      ) : status === 'current' ? (
                        <Icon name="scroll" size={22} />
                      ) : (
                        <Icon name="lock" size={20} />
                      )}
                    </span>

                    <span className="stop__title">{chapter.title}</span>
                    <span className="stop__where">{chapter.where}</span>

                    <span className="stop__chips">
                      <span className="stop__chip">
                        <Icon name="book" size={12} />
                        {chapter.scenes.length}
                      </span>
                      <span className="stop__chip">
                        <Icon name="radiance" size={12} />
                        {chapter.done ? 'earned' : '+30 XP'}
                      </span>
                      <span className="stop__chip stop__chip--era">{chapter.era}</span>
                    </span>

                    <span
                      className="stop__stars"
                      aria-label={`${chapter.stars} of ${MAX_STARS} stars`}
                    >
                      {Array.from({ length: MAX_STARS }, (_, i) => (
                        <Icon
                          key={i}
                          name="star"
                          size={i === 1 ? 21 : 17}
                          className={i < chapter.stars ? 'star is-on' : 'star'}
                        />
                      ))}
                    </span>

                    {isNext ? <span className="stop__cta">Continue</span> : null}
                  </button>
                </div>
              );
            })}
          </section>
        ))}
      </div>
        </>
      )}
    </section>
  );
}
